import {resolveIdentityValidationError} from '@sollapay/utils';
import {z} from 'zod';

import type {CompanyIdError, NationalIdError, PassportError} from '@sollapay/utils';

/**
 * Deliberately narrower than RHF's `RegisterOptions` (which ties `deps` to the specific form's
 * field-name union) so the same rule object can be spread into `register()` for any form shape.
 */
export interface StringFieldRule {
  required: string;
  validate: (value: string) => string | boolean;
}

/** Translates `namespace:key` — pass any `useTranslation()` `t`, `common` is always loaded. */
export type Translate = (key: string) => string;

const NATIONAL_ID_ERROR_KEYS: Record<NationalIdError, string> = {
  invalid_format: 'common:idValidation.invalid_national_id_format',
  invalid_checksum: 'common:idValidation.invalid_national_id_checksum'
};

const COMPANY_ID_ERROR_KEYS: Record<CompanyIdError, string> = {
  invalid_format: 'common:idValidation.invalid_company_id_format',
  invalid_checksum: 'common:idValidation.invalid_company_id_checksum',
  invalid_leading_digit: 'common:idValidation.invalid_company_id_leading_digit'
};

const ID_COUNTRY_REQUIRED_KEY = 'common:idValidation.id_country_required';

const PASSPORT_ERROR_KEYS: Record<PassportError, string> = {
  invalid_format: 'common:idValidation.invalid_passport_format'
};

interface IdentityKind {
  isCorporate: boolean;
  isPassport: boolean;
}

/** The validator choice lives in `@sollapay/utils`; this only picks which kind's copy to show. */
export function resolveIdentityErrorKey(params: IdentityKind & {value: string}): string | null {
  const error = resolveIdentityValidationError(params);
  if (!error) return null;

  const keys: Record<string, string> = params.isCorporate
    ? COMPANY_ID_ERROR_KEYS
    : params.isPassport
      ? PASSPORT_ERROR_KEYS
      : NATIONAL_ID_ERROR_KEYS;
  return keys[error] ?? null;
}

function identityNumberRule(
  t: Translate,
  kind: IdentityKind,
  requiredKey: string
): StringFieldRule {
  return {
    required: t(requiredKey),
    validate: val => {
      if (!val) return true; // `required` already covers emptiness
      const errorKey = resolveIdentityErrorKey({...kind, value: val});
      return !errorKey || t(errorKey);
    }
  };
}

/** Required + Israeli national ID checksum. Shared by buyer/signatory/developer ID fields. */
export function nationalIdRule(t: Translate): StringFieldRule {
  return identityNumberRule(
    t,
    {isCorporate: false, isPassport: false},
    'common:idValidation.national_id_required'
  );
}

/** Required + Israeli company ID checksum. Shared by buyer/organization/developer ID fields. */
export function companyIdRule(t: Translate): StringFieldRule {
  return identityNumberRule(
    t,
    {isCorporate: true, isPassport: false},
    'common:idValidation.company_id_required'
  );
}

function passportRule(t: Translate): StringFieldRule {
  return identityNumberRule(
    t,
    {isCorporate: false, isPassport: true},
    'common:idValidation.passport_required'
  );
}

export function individualIdRule(t: Translate, isPassport: boolean): StringFieldRule {
  return isPassport ? passportRule(t) : nationalIdRule(t);
}

export function idCountryRule(t: Translate): Pick<StringFieldRule, 'required'> {
  return {required: t(ID_COUNTRY_REQUIRED_KEY)};
}

/** Zod `superRefine` counterpart of `individualIdRule`/`idCountryRule` — shared by the seller and buyer schemas. */
export function addIndividualIdentityIssues(
  ctx: z.RefinementCtx,
  params: {
    isCorporate?: boolean;
    isPassport: boolean;
    idNumber: string;
    idCountry?: string;
    idNumberPath: string;
  }
): void {
  const {isCorporate = false, isPassport, idNumber, idCountry = '', idNumberPath} = params;
  const message =
    idNumber.trim() && resolveIdentityErrorKey({isCorporate, isPassport, value: idNumber});
  if (message) {
    ctx.addIssue({code: z.ZodIssueCode.custom, path: [idNumberPath], message});
  }
  if (isPassport && !idCountry.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['idCountry'],
      message: ID_COUNTRY_REQUIRED_KEY
    });
  }
}
